import { useState, useEffect, useCallback } from 'react';
import reactStringReplace from 'react-string-replace';
import _ from 'lodash';
import { getFormRenderConfig } from '@/api/wuji';
import { MonitorRule, Rule } from '../model';
import { renderArray, getTemplateValue } from '../utils';

// 获取模板配置key-value映射
const getConfigMap = (config: Array<any>, field: string, hasDuplicate = false, key = 'value') => {
  const item = config.find((v: any) => v.name === field);
  if (!item) {
    return {};
  }
  try {
    const fieldConfig = JSON.parse(item.componentProps || '{}');
    const { data = [] } = fieldConfig;
    const map = {};
    data.forEach((v: any) => {
      if (!hasDuplicate) {
        // 没有重复的key，直接映射
        map[v[key]] = v;
      } else {
        // 有重复的key，添加到数组中
        if (!map[v[key]]) {
          map[v[key]] = [];
        }
        map[v[key]].push(v);
      }
    }, []);
    return map;
  } catch (error) {
    return {};
  }
};

// 渲染field, operator, period
const renderItem = (config: any, group: string) => {
  if (Array.isArray(config)) {
    if (config.length === 1) {
      return config[0].label;
    }
    return config.find((v: any) => v.group?.includes(group))?.label || '';
  }
  return config?.label;
};

// 渲染values
const renderValue = (config: any, group: string, value: Array<any>) => {
  if (group === 'hidden_value') {
    return '';
  }
  const { suffix = '' } = config.componentProps || {};
  return `${value[0]}${suffix}`;
};

const useMonitorRule = () => {
  const [templateMap, setTemplateMap] = useState<Record<string, any>>({});
  const [configMap, setConfigMap] = useState<Record<'field' | 'period' | 'operator' | 'values', Record<string, any>>>({
    period: {},
    field: {},
    operator: {},
    values: {},
  });
  const [ruleMap, setRuleMap] = useState<Record<string, any>>({});

  // 拉取无极配置
  useEffect(() => {
    getFormRenderConfig({
      filter: 'feature="monitor"&form_name="默认"',
    }).then((res) => {
      const { data = [] } = res || {};
      const templateMap = getConfigMap(data, 'rules', false, 'template_id');
      const periodMap = getConfigMap(data, 'period');
      const fieldMap = getConfigMap(data, 'field');
      const operatorMap = getConfigMap(data, 'operator', true);
      const valuesMap = getConfigMap(data, 'values');
      const ruleMap = getConfigMap(data, 'rule_list', false, 'rule_type');
      setTemplateMap(templateMap);
      setConfigMap({
        field: fieldMap,
        period: periodMap,
        operator: operatorMap,
        values: valuesMap,
      });
      setRuleMap(ruleMap);
    });
  }, []);

  // 渲染单个规则
  const renderRule = useCallback((data: Rule) => {
    const { template } = templateMap[data.template_id] || {};
    if (!template) {
      console.error('模板解析出错：找不到模板', template);
      return '';
    }
    const ruleConfig: Array<any> = [];
    return reactStringReplace(template, /{{(.+?)}}/g, (match) => {
      const { type, index, group } = getTemplateValue(match);
      if (!type || !group || index === undefined) {
        console.error('模板解析出错：获取不到字段类型和序号', type, index);
        return '';
      }

      const value = data.conditions[index]?.[type];
      if (value === undefined) {
        console.error('模板解析出错：获取不到字段值', type, index);
        return '';
      }
      if (configMap[type]) {
        _.set(ruleConfig, `${index}.${type}`, configMap[type][value]);
      }
      switch (type) {
        case 'period':
        case 'field':
        case 'operator':
          return renderItem(configMap[type][value], group);
        case 'values': {
          const config = {
            ...configMap.values[group] || {},
            componentProps: {
              ...configMap.values[group]?.componentProps || {},
              ...ruleConfig[index]?.field?.valuesComponentProps || {},
            },
          };
          return renderValue(config, group, value);
        }
      }
    });
  }, [templateMap, configMap]);

  // 渲染规则列表
  const renderRuleList = useCallback((ruleList: Array<MonitorRule>) => {
    const rules = (ruleList || []).map((v) => v.rules).flat();
    const textArr = rules.map((v) => renderRule(v));
    return renderArray(textArr, { className: 'rule-list' });
  }, [renderRule]);

  // 渲染多条规则
  const renderRules = useCallback((rules: Array<Rule>) => {
    const textArr = (rules || []).map((v) => renderRule(v));
    return renderArray(textArr, { className: 'rule-list' });
  }, [renderRule]);

  // 获取规则名称
  const getRuleName = useCallback((rule_type: string) => {
    return ruleMap[rule_type]?.title || '';
  }, [ruleMap]);

  return {
    ruleMap,
    renderRuleList,
    renderRules,
    getRuleName,
  };
};

export default useMonitorRule;
